import { useEffect, useState, type ReactNode } from 'react';
import { StyleSheet } from 'react-native';

import { useToast } from '@/src/components/Toast';
import { useQueuedCommentReaction } from '@/src/features/offline/use-outbox';
import {
  EditContentModal,
  EditHistoryModal,
} from '@/src/features/posts/ContentEditModals';
import {
  applyReactionTransition,
  createReactionMutationId,
  shouldClearOptimisticReaction,
} from '@/src/features/posts/ReactionControls';
import {
  emptyCommentReactionCounts,
  loadPostCommentEditHistory,
  MAX_COMMENT_BODY_LENGTH,
  type CommentReaction,
  type CommentReactionCounts,
  type PostComment,
  type ProfileMention,
} from '@/src/features/posts/post-service';
import {
  useCommentReaction,
  useSetCommentReaction,
} from '@/src/features/posts/use-comment-reaction';
import { useEditPostComment } from '@/src/features/posts/use-post-comments';
import { spacing } from '@/src/theme/spacing';

import {
  CommentThreadRow,
  ThreadedCommentList,
  type CommentMediaLayout,
} from './CommentThreadRow';
import type { CommentReplyTarget, CommentSortMode } from './comment-threading';

type OptimisticCommentReactionState = {
  baseCounts: CommentReactionCounts;
  baseReaction: CommentReaction | null;
  mutationId: string;
  reaction: CommentReaction | null;
};

function PostCommentThreadRow({
  comment,
  currentUid,
  deleteDisabled,
  isReply = false,
  linkPreviewSource,
  mediaLayout,
  mentionSource,
  onDelete,
  onReply,
  postAuthorUid,
  postId,
  showSyncStatus,
}: {
  comment: PostComment;
  currentUid?: string;
  deleteDisabled?: boolean;
  isReply?: boolean;
  linkPreviewSource: string;
  mediaLayout: CommentMediaLayout;
  mentionSource: string;
  onDelete: (commentId: string) => void;
  onReply: (target: CommentReplyTarget) => void;
  postAuthorUid?: string;
  postId: string;
  showSyncStatus?: boolean;
}) {
  const { showError, showSuccess } = useToast();
  const queuedReaction = useQueuedCommentReaction({
    commentId: comment.id,
    postId,
    uid: currentUid,
  });
  const reactionState = useCommentReaction({
    commentId: comment.id,
    postId,
    uid: currentUid,
  });
  const setReaction = useSetCommentReaction(currentUid);
  const editComment = useEditPostComment();
  const [optimisticReaction, setOptimisticReaction] =
    useState<OptimisticCommentReactionState | null>(null);
  const [editingComment, setEditingComment] = useState(false);
  const [showEditHistory, setShowEditHistory] = useState(false);
  const canDelete =
    currentUid === comment.authorUid || currentUid === postAuthorUid;
  const canEdit = currentUid === comment.authorUid;
  const hasEditHistory = (comment.editCount ?? 0) > 0;
  const baseCounts = comment.reactionCounts ?? emptyCommentReactionCounts();
  const optimisticCounts = optimisticReaction
    ? applyReactionTransition(optimisticReaction)
    : null;
  const queuedCounts =
    queuedReaction !== undefined && !optimisticCounts
      ? applyReactionTransition({
          baseCounts,
          baseReaction: reactionState.reaction,
          reaction: queuedReaction,
        })
      : null;
  const visibleReaction = optimisticReaction
    ? optimisticReaction.reaction
    : queuedReaction !== undefined
      ? queuedReaction
      : reactionState.reaction;
  const visibleReactionCounts =
    optimisticCounts?.counts ?? queuedCounts?.counts ?? baseCounts;
  const reactionDisabled =
    !currentUid ||
    Boolean(comment.syncStatus && comment.syncStatus !== 'synced') ||
    reactionState.loading;

  useEffect(() => {
    setOptimisticReaction(null);
  }, [comment.id, currentUid]);

  useEffect(() => {
    if (!optimisticReaction) {
      return;
    }

    if (
      optimisticCounts &&
      shouldClearOptimisticReaction({
        currentCounts: baseCounts,
        currentMutationAck: reactionState.mutationId,
        expectedCounts: optimisticCounts.counts,
        startingCounts: optimisticReaction.baseCounts,
        persistedReaction: reactionState.reaction,
        queuedReaction,
        targetMutationId: optimisticReaction.mutationId,
        targetReaction: optimisticReaction.reaction,
      })
    ) {
      setOptimisticReaction(null);
    }
  }, [
    baseCounts,
    optimisticCounts,
    optimisticReaction,
    queuedReaction,
    reactionState.mutationId,
    reactionState.reaction,
  ]);

  function chooseReaction(reaction: CommentReaction) {
    if (!currentUid || reactionDisabled) {
      return;
    }

    const nextReaction = visibleReaction === reaction ? null : reaction;
    const mutationId = createReactionMutationId();

    setOptimisticReaction({
      baseCounts:
        optimisticCounts?.counts ?? queuedCounts?.counts ?? baseCounts,
      baseReaction: visibleReaction,
      mutationId,
      reaction: nextReaction,
    });

    setReaction.mutate(
      {
        commentId: comment.id,
        mutationId,
        postId,
        reaction: nextReaction,
      },
      {
        onError: (error) => {
          setOptimisticReaction(null);
          showError(error, 'Unable to update that comment reaction.');
        },
      },
    );
  }

  async function saveCommentEdit(
    nextBody: string,
    nextMentions: ProfileMention[],
  ) {
    try {
      await editComment.mutateAsync({
        body: nextBody,
        commentId: comment.id,
        postId,
        profileMentions: nextMentions,
      });
    } catch (error) {
      showError(error, 'Unable to update that comment.');
      return;
    }

    setEditingComment(false);
    showSuccess('Your comment was updated.', 'Comment saved');
  }

  return (
    <>
      <CommentThreadRow
        canDelete={canDelete}
        canEdit={canEdit}
        comment={comment}
        currentUid={currentUid}
        deleteDisabled={deleteDisabled}
        editDisabled={editComment.isPending}
        isReply={isReply}
        labels={{
          linkPreviewSource,
          mentionSource,
        }}
        mediaLayout={mediaLayout}
        reactionCounts={visibleReactionCounts}
        reactionDisabled={reactionDisabled}
        reactionLoading={false}
        reactionTarget={{
          commentId: comment.id,
          kind: 'comment',
          postId,
        }}
        selectedReaction={visibleReaction}
        showEditHistory={hasEditHistory && Boolean(currentUid)}
        showSyncStatus={showSyncStatus}
        onDelete={() => onDelete(comment.id)}
        onEdit={() => setEditingComment(true)}
        onReply={onReply}
        onSelectReaction={chooseReaction}
        onShowEditHistory={() => setShowEditHistory(true)}
      />
      <EditContentModal
        allowEmpty={Boolean(comment.media)}
        body={comment.body ?? ''}
        loading={editComment.isPending}
        maxLength={MAX_COMMENT_BODY_LENGTH}
        mentions={comment.profileMentions ?? []}
        scope={{ kind: 'comment', postId }}
        title="Edit comment"
        visible={editingComment}
        onClose={() => {
          if (!editComment.isPending) {
            setEditingComment(false);
          }
        }}
        onSave={(nextBody, nextMentions) => {
          void saveCommentEdit(nextBody, nextMentions);
        }}
      />
      <EditHistoryModal
        currentUid={currentUid}
        loadHistory={() =>
          loadPostCommentEditHistory({ commentId: comment.id, postId })
        }
        title="Comment edit history"
        visible={showEditHistory}
        onClose={() => setShowEditHistory(false)}
      />
    </>
  );
}

export function PostCommentThreadList({
  comments,
  currentUid,
  deleteDisabled,
  linkPreviewSource = 'comment_link_preview',
  mediaLayout = 'square',
  mentionSource = 'comment_mention',
  onCommentLayout,
  onDelete,
  onReply,
  orderSessionKey,
  postAuthorUid,
  postId,
  replyComposer,
  replyTarget,
  showSyncStatus,
  sortMode,
}: {
  comments: PostComment[];
  currentUid?: string;
  deleteDisabled?: boolean;
  linkPreviewSource?: string;
  mediaLayout?: CommentMediaLayout;
  mentionSource?: string;
  onCommentLayout?: (commentId: string, y: number) => void;
  onDelete: (commentId: string) => void;
  onReply: (target: CommentReplyTarget) => void;
  orderSessionKey?: boolean | number | string | null;
  postAuthorUid?: string;
  postId: string;
  replyComposer?: ReactNode;
  replyTarget?: CommentReplyTarget | null;
  showSyncStatus?: boolean;
  sortMode?: CommentSortMode;
}) {
  return (
    <ThreadedCommentList
      comments={comments}
      groupStyle={styles.group}
      onCommentLayout={onCommentLayout}
      orderSessionKey={orderSessionKey}
      replyComposer={replyComposer}
      replyTarget={replyTarget}
      sortMode={sortMode}
      renderComment={(comment, isReply) => (
        <PostCommentThreadRow
          comment={comment}
          currentUid={currentUid}
          deleteDisabled={deleteDisabled}
          isReply={isReply}
          linkPreviewSource={linkPreviewSource}
          mediaLayout={mediaLayout}
          mentionSource={mentionSource}
          postAuthorUid={postAuthorUid}
          postId={postId}
          showSyncStatus={showSyncStatus}
          onDelete={onDelete}
          onReply={onReply}
        />
      )}
    />
  );
}

const styles = StyleSheet.create({
  group: {
    gap: spacing.sm,
  },
});
